import ReactDOM from 'react-dom/client';
import type * as Sentry from '@sentry/react';
import './sass/tailwind.css';
import './sass/styles.scss';
import App from './App';
import { Provider } from 'react-redux';
import { store, persistor } from './store/store';
import { loadActiveDiscount } from './slices/discount/discountSlice';
import { logout } from './slices/auth/authSlice';
import { PersistGate } from 'redux-persist/integration/react';
import { ResponsiveSnackbarProvider } from './components/ora/Global/ResponsiveSnackbarProvider';
import MaintenancePage from './pages/Maintenance';
import { resolveMaintenanceHardBlock } from './utils/maintenanceGate';
import { runAfterIdle } from './utils/idle';
import { classifyAssetLoadFailure } from './utils/assetLoadFailure';
import { installAssetLoadFailureRecoveryListeners } from './utils/assetLoadRecovery';
import { isKnownBenignBrowserError } from './utils/isKnownBenignBrowserError';
import {
  scrubReplayRecordingEvent,
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from './utils/telemetryUrlScrub';
import { resolveBackendOverrideFromLocation } from './config/backendUrl';
import { isAutoReloadPending } from './services/clientVersion';
import { runLegacyExchangeOnce } from './services/legacyExchange';
import {
  startSessionSync,
  holdForPendingSignOut,
  syncSessionAfterLegacyExchange,
} from './services/auth';

// ORA-573: consume `?be=<pr>` before the router mounts, and strip the param
// from the URL so it does not leak into later navigation. Every consumer reads
// the base URL per request, so an override set here is picked up by requests
// issued from any module-load side effect that already ran. No-ops entirely in
// production builds.
resolveBackendOverrideFromLocation();

// Start the one-time legacy token exchange as early as possible; the axios
// client and the persist gate both wait on this same attempt.
void runLegacyExchangeOnce();

// Follow sign-ins, sign-outs and impersonation switches made in other tabs:
// they all share this tab's session cookie.
startSessionSync();

const VENDOR_FRAME_FILENAME =
  /node_modules|react-dom|floating-ui-vendor|@sentry|@posthog|rrweb-record/i;

const expectedAuthErrorMessages = new Set([
  'Invalid refresh token',
  'Refresh token expired',
  'Refresh token is required',
  'No refresh token available',
  'Refresh token invalid or expired',
]);

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;

const getStringField = (
  record: Record<string, unknown> | null,
  key: string
): string | null => {
  const value = record?.[key];
  return typeof value === 'string' ? value : null;
};

const getNumberField = (
  record: Record<string, unknown> | null,
  key: string
): number | null => {
  const value = record?.[key];
  return typeof value === 'number' ? value : null;
};

const getOriginalExceptionRecord = (hint?: Sentry.EventHint) =>
  asRecord(hint?.originalException);

const shouldDropExpectedAuthErrors = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const originalException = hint?.originalException as
    { isExpectedAuthError?: boolean; message?: string } | undefined;

  if (originalException?.isExpectedAuthError) {
    return true;
  }

  const originalMessage =
    typeof originalException?.message === 'string'
      ? originalException.message
      : undefined;

  if (originalMessage && expectedAuthErrorMessages.has(originalMessage)) {
    return true;
  }

  const eventMessage = event.exception?.values?.[0]?.value;
  if (typeof eventMessage === 'string') {
    return expectedAuthErrorMessages.has(eventMessage);
  }
  return false;
};

const shouldDropClientUpdateRequiredNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const originalException = getOriginalExceptionRecord(hint);
  const response = asRecord(originalException?.response);
  const responseData = asRecord(response?.data);
  const status = getNumberField(response, 'status');
  const code = getStringField(responseData, 'code');
  const message = extractEventText(event, hint);

  return (
    status === 426 ||
    code === 'CLIENT_UPDATE_REQUIRED' ||
    /request failed with status code 426|client_update_required/i.test(message)
  );
};

const shouldDropAssetLoadFailureNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) =>
  Boolean(
    classifyAssetLoadFailure([
      hint?.originalException,
      event.message,
      ...(event.breadcrumbs?.flatMap((breadcrumb) => [
        breadcrumb.message,
        breadcrumb.data,
      ]) ?? []),
      ...(event.exception?.values?.flatMap((value) => [
        value.type,
        value.value,
        value.stacktrace?.frames?.map((frame) => frame.filename),
      ]) ?? []),
    ])
  );

const publicRouteUnhandledRejectionPattern =
  /Object Not Found Matching Id:\d+,\s*MethodName:update,\s*ParamCount:4/i;

const customEventUnhandledRejectionPattern =
  /Event [`'"]?CustomEvent[`'"]? \(type=unhandledrejection\) captured as promise rejection/i;

const emptyObjectUnhandledRejectionValue =
  'Object captured as promise rejection with keys: [object has no keys]';

const resolveEventPathname = (event: Sentry.Event) => {
  const requestUrl = event.request?.url;

  if (typeof requestUrl === 'string' && requestUrl) {
    try {
      return new URL(
        requestUrl,
        typeof window !== 'undefined' ? window.location.origin : undefined
      ).pathname;
    } catch {
      if (requestUrl.startsWith('/')) {
        return requestUrl.split('?')[0];
      }
    }
  }

  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }

  return null;
};

const hasAppStackTrace = (event: Sentry.Event) =>
  Boolean(
    event.exception?.values?.some(
      (value) => (value.stacktrace?.frames?.length ?? 0) > 0
    )
  );

const extractUnhandledRejectionText = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const originalException = hint?.originalException as
    { message?: string; reason?: unknown } | string | undefined;

  const reasonText =
    typeof originalException === 'object' &&
    typeof originalException?.reason === 'string'
      ? originalException.reason
      : undefined;

  const originalMessage =
    typeof originalException === 'string'
      ? originalException
      : originalException?.message;

  // NOTE: exception `type` and `value` end up SPACE-joined here
  // ("NotFoundError The object can not be found here."), never "Type: value".
  // Any filter regex matching across that boundary must treat the colon as
  // optional — a mandatory colon silently never matches production events.
  const eventMessages = [
    event.message,
    ...(event.exception?.values?.flatMap((value) => [
      value.type,
      value.value,
    ]) ?? []),
  ];

  return [reasonText, originalMessage, ...eventMessages]
    .filter((value): value is string => Boolean(value))
    .join(' ');
};

const shouldDropPublicRouteUnhandledRejectionNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const pathname = resolveEventPathname(event);

  const isTargetRoute =
    pathname === '/email-preferences' || pathname === '/auth';

  if (!isTargetRoute) {
    return false;
  }

  if (event.user?.id || event.user?.email || hasAppStackTrace(event)) {
    return false;
  }

  return publicRouteUnhandledRejectionPattern.test(
    extractUnhandledRejectionText(event, hint)
  );
};

const emptyRangesPattern = /\bEmptyRanges\b/i;

const extractEventText = (event: Sentry.Event, hint?: Sentry.EventHint) => {
  const originalException = hint?.originalException as
    { message?: string; reason?: unknown } | string | undefined;

  const reasonText =
    typeof originalException === 'object' &&
    typeof originalException?.reason === 'string'
      ? originalException.reason
      : undefined;

  const originalMessage =
    typeof originalException === 'string'
      ? originalException
      : originalException?.message;

  return [
    reasonText,
    originalMessage,
    extractUnhandledRejectionText(event, hint),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ');
};

const getNavigatorUserAgent = () =>
  typeof navigator !== 'undefined' ? navigator.userAgent : '';

const getBrowserContextName = (event: Sentry.Event) => {
  const browserContext = event.contexts?.browser as
    { name?: unknown } | undefined;

  return typeof browserContext?.name === 'string' ? browserContext.name : null;
};

const isEmbeddedIosWebViewUserAgent = (userAgent: string) =>
  /(iPhone|iPad|iPod)/i.test(userAgent) &&
  /AppleWebKit/i.test(userAgent) &&
  !/Safari/i.test(userAgent);

const resolveBrowserRuntimeInfo = (event: Sentry.Event) => {
  const browserName = getBrowserContextName(event);
  const userAgent = getNavigatorUserAgent();

  const browserIdentity = [browserName, userAgent]
    .filter((value): value is string => Boolean(value))
    .join(' ');

  const isSafariLike =
    /Apple Mail|WKWebView|Safari/i.test(browserIdentity) &&
    !/Chrome|Chromium|CriOS|FxiOS|EdgiOS/i.test(browserIdentity);

  const isEmbeddedBrowser =
    /Apple Mail|WKWebView/i.test(browserIdentity) ||
    isEmbeddedIosWebViewUserAgent(userAgent);

  return {
    browserName,
    userAgent,
    isSafariLike,
    isEmbeddedBrowser,
  };
};

const annotateBrowserRuntime = <TEvent extends Sentry.Event>(event: TEvent) => {
  const browserRuntime = resolveBrowserRuntimeInfo(event);

  event.tags = {
    ...event.tags,
    browserRuntime: browserRuntime.browserName ?? 'unknown',
    isEmbeddedBrowser: browserRuntime.isEmbeddedBrowser ? 'true' : 'false',
    isSafariLikeBrowser: browserRuntime.isSafariLike ? 'true' : 'false',
  };

  event.contexts = {
    ...event.contexts,
    oraBrowserRuntime: {
      pathname: resolveEventPathname(event) ?? '',
      userAgent: browserRuntime.userAgent,
      browserName: browserRuntime.browserName ?? '',
      isEmbeddedBrowser: browserRuntime.isEmbeddedBrowser,
      isSafariLike: browserRuntime.isSafariLike,
    },
  };

  return event;
};

const hasOnlyMissingSourceFrames = (event: Sentry.Event) => {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? [];

  if (frames.length === 0) {
    return true;
  }

  return frames.every(
    (frame) => !frame.filename || frame.filename === 'undefined'
  );
};

const getStackFrameFilenames = (event: Sentry.Event) =>
  event.exception?.values?.flatMap(
    (value) =>
      value.stacktrace?.frames?.map((frame) => frame.filename ?? '') ?? []
  ) ?? [];

const getStackFrames = (event: Sentry.Event) =>
  event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ??
  [];

const hasLikelyApplicationStackFrame = (event: Sentry.Event) =>
  getStackFrames(event).some((frame) => {
    const frameRecord = frame as Record<string, unknown>;
    if (frameRecord.in_app === true || frameRecord.inApp === true) {
      return true;
    }

    const filename = frame.filename ?? '';
    if (!filename) {
      return false;
    }

    return !VENDOR_FRAME_FILENAME.test(filename);
  });

const shouldDropEmptyRangesNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const eventText = extractEventText(event, hint);
  if (!emptyRangesPattern.test(eventText)) {
    return false;
  }

  return hasOnlyMissingSourceFrames(event);
};

const shouldDropUnknownCustomEventUnhandledRejectionNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  if (event.user?.id || event.user?.email || hasAppStackTrace(event)) {
    return false;
  }

  return customEventUnhandledRejectionPattern.test(
    extractEventText(event, hint)
  );
};

const hasUsefulOriginalException = (hint?: Sentry.EventHint) => {
  const originalException = hint?.originalException;

  if (originalException === undefined || originalException === null) {
    return false;
  }

  if (typeof originalException === 'string') {
    return originalException.trim().length > 0;
  }

  if (originalException instanceof Error) {
    return Boolean(originalException.message || originalException.stack);
  }

  if (typeof originalException !== 'object') {
    return true;
  }

  return Object.keys(originalException).length > 0;
};

const shouldDropEmptyObjectUnhandledRejectionNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  if (event.user?.id || event.user?.email || hasAppStackTrace(event)) {
    return false;
  }

  if (hasUsefulOriginalException(hint)) {
    return false;
  }

  return Boolean(
    event.exception?.values?.some(
      (value) =>
        value.type === 'UnhandledRejection' &&
        value.value === emptyObjectUnhandledRejectionValue &&
        value.mechanism?.type === 'onunhandledrejection'
    )
  );
};

const shouldDropPosthogIframeNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const eventText = extractEventText(event, hint);
  const stackFrameFilenames = getStackFrameFilenames(event);

  return (
    /blocked a frame with origin/i.test(eventText) &&
    (/@posthog\/rrweb-record|rrweb-record/i.test(eventText) ||
      stackFrameFilenames.some((filename) =>
        /@posthog\/rrweb-record|rrweb-record/i.test(filename)
      ))
  );
};

const shouldDropSentryReplayIframeNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const eventText = extractEventText(event, hint);
  const stackFrameFilenames = getStackFrameFilenames(event);

  return (
    /blocked a frame with origin/i.test(eventText) &&
    (/@sentry\/replay|sentry\/replay/i.test(eventText) ||
      stackFrameFilenames.some((filename) =>
        /@sentry\/replay|sentry\/replay/i.test(filename)
      ))
  );
};

const shouldDropExpectedBrowserRuntimeNoise = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) => {
  const eventText = extractEventText(event, hint);
  const pathname = resolveEventPathname(event);
  const browserRuntime = resolveBrowserRuntimeInfo(event);

  if (
    /Fullscreen request denied|Pending operation cancelled by requestFullscreen/i.test(
      eventText
    )
  ) {
    return true;
  }

  if (/No active window to close/i.test(eventText)) {
    return true;
  }

  if (
    /AbortError:?\s*The operation was aborted/i.test(eventText) &&
    browserRuntime.isEmbeddedBrowser &&
    !hasLikelyApplicationStackFrame(event)
  ) {
    return true;
  }

  if (
    /IndexSizeError:\s*The index is not in the allowed range/i.test(
      eventText
    ) &&
    pathname === '/study/video' &&
    browserRuntime.isSafariLike
  ) {
    return true;
  }

  if (
    /Maximum call stack size exceeded/i.test(eventText) &&
    pathname === '/email-preferences' &&
    hasOnlyMissingSourceFrames(event)
  ) {
    return true;
  }

  // ORA-264: Blink's "Failed to execute 'removeChild' on 'Node': The node to be
  // removed is not a child of this node" used to be dropped here. It is the
  // commit-phase DOM failure from ORA-262, which leaves the page painted but
  // inert — the sibling of the WebKit wording removed from
  // `isKnownBenignBrowserError` in the same change; see the note there.
  //
  // Unlike that one, this rule was barely doing anything. Its gate,
  // `hasLikelyApplicationStackFrame`, short-circuits on `in_app === true`, and
  // @sentry/browser's parser marks every browser frame `in_app: true`
  // unconditionally — so the filename deny-list below it is unreachable and the
  // rule could only fire on events with no parsed frames at all. ORA-FE-R1 kept
  // receiving events the whole time it was installed, which is the observable
  // proof. Its counts are therefore a sound baseline for sizing the WebKit
  // population, and removing this is about intent, not recovered volume.

  return false;
};

// Stricter than hasLikelyApplicationStackFrame: a "real" first-party source
// frame must point at an actual source file — not vendor code, not "[native
// code]", not the document URL, not a sourceless frame. Used to tell
// React-internal/native stacks (safe to drop) from genuine app stacks.
const hasRealApplicationSourceFrame = (event: Sentry.Event) =>
  getStackFrames(event).some((frame) => {
    const filename = frame.filename ?? '';
    if (!filename || filename === 'undefined' || filename === '[native code]') {
      return false;
    }

    if (!filename.includes('/') && !/\.(t|j)sx?($|\?)/i.test(filename)) {
      return false;
    }

    return !VENDOR_FRAME_FILENAME.test(filename);
  });

const shouldDropKnownBenignBrowserError = (
  event: Sentry.Event,
  hint?: Sentry.EventHint
) =>
  isKnownBenignBrowserError({
    message: extractEventText(event, hint),
    pathname: resolveEventPathname(event),
    isEmbeddedBrowser: resolveBrowserRuntimeInfo(event).isEmbeddedBrowser,
    isSafariLike: resolveBrowserRuntimeInfo(event).isSafariLike,
    hasRealApplicationSourceFrame: hasRealApplicationSourceFrame(event),
    hasOnlySourcelessFrames: hasOnlyMissingSourceFrames(event),
    frameFunctions: getStackFrames(event).map((frame) => frame.function ?? ''),
    frameFilenames: getStackFrameFilenames(event),
  });

if (process.env.NODE_ENV === 'production') {
  installAssetLoadFailureRecoveryListeners();

  runAfterIdle(() => {
    void import('@sentry/react').then((Sentry) => {
      Sentry.init({
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
        dsn: 'https://f21b1d1c5696794bb5582a3f585b1dd8@o1186798.ingest.us.sentry.io/4507020246450176',
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            // Replays must not show tokens, emails or answers (ORA-1089).
            maskAllText: true,
            beforeAddRecordingEvent: (event) =>
              scrubReplayRecordingEvent(event),
            blockAllMedia: false,
          }),
        ],
        tracesSampleRate: 0.1,
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/yourserver\.io\/api/,
        ],
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        denyUrls: [
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-web-extension:\/\//i,
          /safariextension/i,
          /\.appex\//i,
        ],
        beforeSend(event, hint) {
          // Navigation has begun; errors during the reload window are noise.
          if (isAutoReloadPending()) return null;

          const annotatedEvent = annotateBrowserRuntime(event);

          if (shouldDropExpectedAuthErrors(annotatedEvent, hint)) return null;
          if (shouldDropClientUpdateRequiredNoise(annotatedEvent, hint))
            return null;
          if (shouldDropAssetLoadFailureNoise(annotatedEvent, hint))
            return null;
          if (
            shouldDropPublicRouteUnhandledRejectionNoise(annotatedEvent, hint)
          )
            return null;
          if (shouldDropEmptyRangesNoise(annotatedEvent, hint)) return null;
          if (
            shouldDropUnknownCustomEventUnhandledRejectionNoise(
              annotatedEvent,
              hint
            )
          )
            return null;
          if (
            shouldDropEmptyObjectUnhandledRejectionNoise(annotatedEvent, hint)
          )
            return null;
          if (shouldDropPosthogIframeNoise(annotatedEvent, hint)) return null;
          if (shouldDropSentryReplayIframeNoise(annotatedEvent, hint))
            return null;
          if (shouldDropExpectedBrowserRuntimeNoise(annotatedEvent, hint))
            return null;
          if (shouldDropKnownBenignBrowserError(annotatedEvent, hint))
            return null;
          // Scrub reset, verification and sign-in URL tokens (ORA-1089).
          return scrubSentryEvent(annotatedEvent);
        },
        beforeSendTransaction: (event) => scrubSentryEvent(event),
        beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
      });

      // Scrub replay URLs, which bypass beforeSend (ORA-1089).
      Sentry.getClient()?.on('preprocessEvent', (event) => {
        if (event.type === 'replay_event') scrubSentryEvent(event);
      });
    });
  });
}

const shouldHardBlockForMaintenance = resolveMaintenanceHardBlock();
const root = ReactDOM.createRoot(document.getElementById('root')!);

if (shouldHardBlockForMaintenance) {
  root.render(<MaintenancePage />);
} else {
  root.render(
    <Provider store={store}>
      <PersistGate
        loading={null}
        persistor={persistor}
        onBeforeLift={async () => {
          // A Log Out whose revoke never landed is retried before anything
          // can sign this browser back in with its cookie (ORA-1089).
          await holdForPendingSignOut();

          // A browser still holding legacy tokens trades them for a session
          // cookie before the app renders (ORA-987). Never rejects.
          const legacyExchange = await runLegacyExchangeOnce();

          // A refused legacy token (expired, or 403 TWO_FACTOR_REQUIRED for
          // an account that has since enabled 2FA) is a signed-out browser:
          // go straight to sign-in rather than render a session that is gone.
          if (
            legacyExchange === 'rejected' &&
            store.getState().auth.isAuthenticated
          ) {
            store.dispatch(logout());
          }

          // A trade set a cookie for an account the persisted state may not
          // reflect (a stale or missing copy, or an admin who was
          // impersonating and got their own session back): ask the server
          // who this is.
          if (legacyExchange === 'exchanged') {
            await syncSessionAfterLegacyExchange();
          }

          if (store.getState().auth.isAuthenticated) {
            // Preload discount once after rehydration to avoid duplicate requests from views
            store.dispatch(loadActiveDiscount());
          }
        }}
      >
        <ResponsiveSnackbarProvider>
          <App />
        </ResponsiveSnackbarProvider>
      </PersistGate>
    </Provider>
  );
}
